import type { Address, Hex, PublicClient } from "viem";

/**
 * Canonical V2 init code pair hash.
 * keccak256(type(UniswapV2Pair).creationCode) — same across Ethereum & Base.
 */
const V2_INIT_CODE_PAIR_HASH: Record<number, Hex> = {
  1: "0x96e8ac4277198ff8b6f785478aa9a39f403cb768dd02cbee326c3e7da348845f",
  8453: "0x96e8ac4277198ff8b6f785478aa9a39f403cb768dd02cbee326c3e7da348845f",
};

/** Get the V2 init code pair hash for a given chain, or null if unknown. */
export function getV2InitCodePairHash(chainId: number): Hex | null {
  return V2_INIT_CODE_PAIR_HASH[chainId] ?? null;
}

/** Result of checking a single contract address. */
export interface ContractCheck {
  name: string;
  address: Address;
  found: boolean;
}

/** Known Uniswap contract addresses on mainnet chains. */
const CANONICAL_ADDRESSES: Record<number, Record<string, Address>> = {
  // Ethereum mainnet
  1: {
    weth: "0xC02aaA39b223FE8D0A0e5C4F27eAD9083C756Cc2",
    permit2: "0x000000000022D473030F116dDEE9F6B43aC78BA3",
    v2Factory: "0x5C69bEe701ef814a2B6a3EDD4B1652CB9cc5aA6f",
    v2Router: "0x7a250d5630B4cF539739dF2C5dAcb4c659F2488D",
    v3Factory: "0x1F98431c8aD98523631AE4a59f267346ea31F984",
    v3Router: "0xE592427A0AEce92De3Edee1F18E0157C05861564",
    v3NonfungiblePositionManager: "0xC36442b4a4522E871399CD717aBDD847Ab11FE88",
    v3QuoterV2: "0x61fFE014bA17989E743c5F6cB21bF9697530B21e",
    universalRouter: "0x66a9893cC07d91D95644aedd05D03f95e1dBA8aF",
    v4PoolManager: "0x000000000004444c5dc75cB358380D2e3dE08A90",
    v4PositionManager: "0xbD216513d74C8cf14cf4747E6AaA6420ff64ee9e",
    v4Quoter: "0x52F0E24D1c21C8A0cB1e5a5dD6198556BD9E1203",
    v4StateView: "0x7FFe42C4a5DEeA5b0feC41C94c136Cf115597227",
    // UniswapX reactors (Ethereum)
    exclusiveDutchOrderReactor: "0x6000da47483062A0D734Ba3dc7576Ce6A0B645C4",
    v2DutchOrderReactor: "0x00000011F84B9aa48e5f8aA8B9897600006289Be",
    orderQuoter: "0x54539967a06Fc0E3C3ED0ee320Eb67362D13C5fF",
  },
  // Base
  8453: {
    weth: "0x4200000000000000000000000000000000000006",
    permit2: "0x000000000022D473030F116dDEE9F6B43aC78BA3",
    v2Factory: "0x8909Dc15e40173Ff4699343b6eB8132c65e18eC6",
    v2Router: "0x4752ba5DBc23f44D87826276BF6Fd6b1C372aD24",
    v3Factory: "0x33128a8fC17869897dcE68Ed026d694621f6FDfD",
    v3Router: "0x2626664c2603336E57B271c5C0b26F421741e481",
    v3NonfungiblePositionManager: "0x03a520b32C04BF3bEEf7BEb72E919cf822Ed34f1",
    v3QuoterV2: "0x3d4e44Eb1374240CE5F1B871ab261CD16335B76a",
    universalRouter: "0x6ff5693b99212Da76ad316178A184AB56D299b43",
    v4PoolManager: "0x498581fF718922c3f8e6A244956aF099B2652b2b",
    v4PositionManager: "0x7C5f5a4bBd8fd63184577525326123B519429bDc",
    v4Quoter: "0x0d5e0f971ED27FBff6c2837bf31316121532048D",
    v4StateView: "0xA3c0c9b65baD0b08107Aa264b0f3dB444b867A71",
    // UniswapX reactors (Base)
    priorityOrderReactor: "0x000000001Ec5656dcdB24D90DFa42742738De729",
    orderQuoter: "0x88440407634f89873c5d9439987ac4be9725fea8",
  },
};

/**
 * Check if a contract exists at an address via eth_getCode.
 */
async function checkContract(
  client: PublicClient,
  name: string,
  address: Address,
): Promise<ContractCheck> {
  try {
    const code = await client.getCode({ address });
    return { name, address, found: !!code && code !== "0x" };
  } catch {
    return { name, address, found: false };
  }
}

/**
 * Discover Uniswap contracts at canonical addresses on a forked chain.
 *
 * Runs parallel eth_getCode checks for all known contracts (~2s).
 * Returns found and missing contracts.
 */
export async function discoverContracts(
  client: PublicClient,
  chainId: number,
): Promise<{
  found: ContractCheck[];
  missing: ContractCheck[];
  addresses: Record<string, Address>;
}> {
  const contracts = CANONICAL_ADDRESSES[chainId];
  if (!contracts) {
    return { found: [], missing: [], addresses: {} };
  }

  const checks = await Promise.all(
    Object.entries(contracts).map(([name, address]) =>
      checkContract(client, name, address),
    ),
  );

  const found = checks.filter((c) => c.found);
  const missing = checks.filter((c) => !c.found);

  const addresses: Record<string, Address> = {};
  for (const check of found) {
    addresses[check.name] = check.address;
  }

  return { found, missing, addresses };
}
